<template lang="pug">
.hierarchical-multi-select-dropdown.relative
  //- Label
  label.block.mb-0(v-if="label" :for="inputId")
    | {{ label }}
    span.text-red-500.required-asterisk(v-if="required") *
  
  //- Instructional text
  p.text-sm.text-gray-500.dark_text-gray-400.mb-2.mt-0(v-if="instructionalText")
    | {{ instructionalText }}
  
  //- Selected items as chips
  .selected-chips.mb-2(v-if="selectedItems.length > 0")
    .flex.flex-wrap.gap-2
      .chip.flex.items-center.gap-1.px-3.py-1.bg-blue-700.text-white.rounded-full.text-sm.transition-all.duration-200(
        v-for="item in selectedItems" 
        :key="item"
      )
        span {{ item }}
        button.remove-chip.w-4.h-4.flex.items-center.justify-center.rounded-full.hover_bg-blue-600.transition-all.duration-200(
          @click="removeItem(item)"
          type="button"
          aria-label="Remove item"
        )
          Icon(icon="mdi:close" class="w-3 h-3")
  
  //- Dropdown trigger
  .dropdown-trigger.relative
    input.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded.text-gray-900.dark_text-white(
      :id="inputId"
      :value="searchQuery"
      @input="updateSearch"
      @click.stop="openDropdown"
      @keydown="handleKeydown"
      :placeholder="placeholder"
      :disabled="disabled"
      :class="{ 'opacity-50 cursor-not-allowed': disabled }"
      ref="inputRef"
    )
    button.dropdown-arrow.absolute.right-2.transform.p-1.transition-transform.duration-200(
      @click.stop="toggleDropdown"
      type="button"
      :disabled="disabled"
      :class="{ 'opacity-50 cursor-not-allowed': disabled }"
      style="top: 50%; transform: translateY(-50%);"
    )
      Icon(
        :icon="isOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'" 
        class="w-4 h-4 text-gray-500"
      )
  
  //- Dropdown menu
  .dropdown-menu.absolute.top-full.left-0.right-0.mt-1.bg-gray-700.border.border-gray-600.rounded.shadow-lg.z-50.max-h-60.overflow-y-auto.dark_bg-gray-700.dark_border-gray-600.bg-white.border-gray-300(
    v-if="isOpen"
    ref="dropdownRef"
    @click.stop
  )
    .p-2
      //- Render hierarchical tree
      .jurisdiction-tree
        JurisdictionTreeNode(
          v-for="(node, nodeId) in filteredTree" 
          :key="nodeId"
          :node="node"
          :jurisdictions="props.jurisdictions"
          :selectedItems="selectedItems"
          :expandedNodes="expandedNodes"
          :depth="0"
          @toggle="toggleOption"
          @toggle-expand="toggleNode"
        )
      
      //- No results message
      .no-results.p-2.text-sm.text-gray-400.dark_text-gray-400.text-gray-500.text-center(
        v-if="Object.keys(filteredTree).length === 0"
      )
        | No jurisdictions found

  //- Click outside to close
  .fixed.inset-0.z-40(v-if="isOpen" @click="closeDropdown")
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { Icon } from '@iconify/vue';
// @ts-ignore - Vue 3 script setup components are auto-exported
import JurisdictionTreeNode from './JurisdictionTreeNode.vue';

interface JurisdictionNode {
  id: string;
  name: string;
  parentId: string | null;
}

interface Props {
  modelValue: string[] | string;
  jurisdictions: Record<string, JurisdictionNode>;
  dataType: 'comma-separated' | 'array';
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  instructionalText?: string;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Select jurisdictions',
  label: '',
  required: false,
  disabled: false,
  instructionalText: ''
});

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: string[] | string];
  'update:jurisdictionIds': [value: string[]];
}>();

// Reactive state
const isOpen = ref(false);
const searchQuery = ref('');
const expandedNodes = ref(new Set<string>());
const inputRef = ref<HTMLInputElement>();
const dropdownRef = ref<HTMLElement>();

// Generate unique ID for accessibility
const inputId = `hierarchical-multi-select-${Math.random()
  .toString(36)
  .substr(2, 9)}`;

// Normalize input value to array
const normalizeInput = (
  value: string[] | string,
  dataType: string
): string[] => {
  if (dataType === 'comma-separated' && typeof value === 'string') {
    if (!value) return [];
    return value
      .split(',')
      .map(item => item.trim())
      .filter(item => item);
  }
  return Array.isArray(value) ? value : [];
};

// Normalize output value based on dataType
const normalizeOutput = (
  value: string[],
  dataType: string
): string[] | string => {
  if (dataType === 'comma-separated') {
    return value.join(', ');
  }
  return value;
};

// Computed properties
const selectedItems = computed(() => {
  return normalizeInput(props.modelValue, props.dataType);
});

// Helper function to get all ancestors of a node (for auto-expand on search)
const getAncestorChain = (nodeId: string): string[] => {
  const chain: string[] = [];
  let currentNode = props.jurisdictions[nodeId];

  while (currentNode && currentNode.parentId !== null) {
    chain.push(currentNode.parentId);
    currentNode = props.jurisdictions[currentNode.parentId];
  }

  return chain;
};

// Filter tree based on search query
const filteredTree = computed(() => {
  if (!searchQuery.value) {
    // Return only top-level nodes (nodes with parentId: null)
    const topLevelNodes: Record<string, JurisdictionNode> = {};
    for (const [id, node] of Object.entries(props.jurisdictions)) {
      if (node.parentId === null) {
        topLevelNodes[id] = node;
      }
    }
    return topLevelNodes;
  }

  const searchLower = searchQuery.value.toLowerCase();
  const filtered: Record<string, JurisdictionNode> = {};

  // Only show nodes that match the search query
  const matchingNodes = Object.values(props.jurisdictions).filter(node =>
    node.name.toLowerCase().includes(searchLower)
  );

  // Include matching nodes and their entire parent chain (for hierarchy display)
  for (const node of matchingNodes) {
    // Add the matching node itself
    filtered[node.id] = node;

    // Add all ancestors (parent chain) for hierarchy display
    const ancestorChain = getAncestorChain(node.id);
    for (const ancestorId of ancestorChain) {
      const ancestor = props.jurisdictions[ancestorId];
      if (ancestor) {
        filtered[ancestor.id] = ancestor;
      }
    }
  }

  return filtered;
});

// Helper function to check if a node is selected (for backward compatibility if needed)
const isSelected = (node: JurisdictionNode): boolean => {
  return selectedItems.value.includes(node.name);
};

// Helper function to get IDs from selected names
const getIdsFromNames = (names: string[]): string[] => {
  const ids: string[] = [];

  for (const name of names) {
    const node = Object.values(props.jurisdictions).find(
      node => node.name === name
    );
    if (node) {
      ids.push(node.id);
    }
  }

  return ids;
};

// Methods
const updateSearch = (event: Event) => {
  const target = event.target as HTMLInputElement;
  searchQuery.value = target.value;
};

const openDropdown = () => {
  if (!props.disabled) {
    isOpen.value = true;
  }
};

const closeDropdown = () => {
  isOpen.value = false;
  searchQuery.value = '';
};

const toggleDropdown = () => {
  if (isOpen.value) {
    closeDropdown();
  } else {
    openDropdown();
  }
};

const toggleNode = (nodeId: string) => {
  if (expandedNodes.value.has(nodeId)) {
    expandedNodes.value.delete(nodeId);
  } else {
    expandedNodes.value.add(nodeId);
  }
};

const toggleOption = (node: JurisdictionNode) => {
  const currentSelected = [...selectedItems.value];
  const isCurrentlySelected = currentSelected.includes(node.name);

  if (isCurrentlySelected) {
    // Remove only this node (no descendant logic)
    const index = currentSelected.indexOf(node.name);
    currentSelected.splice(index, 1);
  } else {
    // Add only this node (no descendant logic)
    currentSelected.push(node.name);
  }

  emit('update:modelValue', normalizeOutput(currentSelected, props.dataType));
  emit('update:jurisdictionIds', getIdsFromNames(currentSelected));

  // Keep dropdown open
  if (!isOpen.value) {
    isOpen.value = true;
  }
};

const removeItem = (item: string) => {
  // Remove only the specific item (no orphaned parent cleanup)
  const currentSelected = selectedItems.value.filter(
    selected => selected !== item
  );

  emit('update:modelValue', normalizeOutput(currentSelected, props.dataType));
  emit('update:jurisdictionIds', getIdsFromNames(currentSelected));

  // Keep dropdown open
  if (!isOpen.value) {
    isOpen.value = true;
  }
};

const handleKeydown = (event: KeyboardEvent) => {
  if (!isOpen.value) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openDropdown();
    }
    return;
  }

  switch (event.key) {
    case 'Escape':
      event.preventDefault();
      closeDropdown();
      inputRef.value?.blur();
      break;
  }
};

// Watch for external changes to modelValue
watch(
  () => props.modelValue,
  () => {
    // Reset search when modelValue changes externally
    if (!isOpen.value) {
      searchQuery.value = '';
    }
  },
  { deep: true }
);

// Watch for search query changes and auto-expand parent chains
watch(
  () => searchQuery.value,
  newQuery => {
    if (!newQuery) {
      // Clear expanded nodes when search is cleared (optional - remove if you want to keep expanded state)
      // expandedNodes.value.clear();
      return;
    }

    // Auto-expand parent chain for matching nodes
    const searchLower = newQuery.toLowerCase();
    const matchingNodes = Object.values(props.jurisdictions).filter(node =>
      node.name.toLowerCase().includes(searchLower)
    );

    // Expand all ancestors of matching nodes
    for (const node of matchingNodes) {
      const ancestorChain = getAncestorChain(node.id);
      for (const ancestorId of ancestorChain) {
        expandedNodes.value.add(ancestorId);
      }
    }
  }
);

// Click outside to close
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Node;
  if (
    dropdownRef.value &&
    !dropdownRef.value.contains(target) &&
    !inputRef.value?.contains(target)
  ) {
    closeDropdown();
  }
};

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.checkbox {
  accent-color: #3b82f6;
}

.jurisdiction-node {
  transition: background-color 0.15s ease-in-out;
}

.expand-icon {
  transition: transform 0.15s ease-in-out;
}

.expand-icon:hover {
  transform: scale(1.1);
}

/* Custom scrollbar for dropdown */
.dropdown-menu::-webkit-scrollbar {
  width: 6px;
}

.dropdown-menu::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.dropdown-menu::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.dropdown-menu::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Dark mode scrollbar */
.dark .dropdown-menu::-webkit-scrollbar-track {
  background: #374151;
}

.dark .dropdown-menu::-webkit-scrollbar-thumb {
  background: #6b7280;
}

.dark .dropdown-menu::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>
